import { useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  FileText,
  HandHeart,
  LineChart,
  User,
  type LucideIcon,
} from "lucide-react";
import type { CaseSummary } from "@/lib/api";
import { computeSnapshot, type SnapshotMetrics } from "@/lib/morningSnapshot";
import { useDashboardSummary } from "@/hooks/useDashboardSummary";
import { DEFAULT_VERTICAL, isRecruiting, type Vertical } from "@/lib/verticalCopy";
import type { StatusKind } from "@/lib/caseStatus";

const STORAGE_KEY = "dv-morning-snapshot-open-v1";

/**
 * Render avg time-to-submit in the largest sensible unit so sub-day
 * completions (e.g. 10 minutes) don't collapse to "0.0d". Thresholds:
 *   < 1 min   → "<1m"
 *   < 1 hour  → "Xm"
 *   < 1 day   → "Xh Ym" (drops minutes when 0)
 *   ≥ 1 day   → "Xd Yh" (drops hours when 0)
 */
function formatAvgTime(days: number | null): string {
  if (days === null || !Number.isFinite(days) || days < 0) return "—";
  const totalMinutes = Math.round(days * 24 * 60);
  if (totalMinutes < 1) return "<1m";
  if (totalMinutes < 60) return `${totalMinutes}m`;
  if (totalMinutes < 60 * 24) {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }
  const totalHours = Math.round(days * 24);
  const d = Math.floor(totalHours / 24);
  const h = totalHours % 24;
  return h === 0 ? `${d}d` : `${d}d ${h}h`;
}

export type SnapshotFilter = StatusKind | "needs_attention" | "waiting_on_vouch";

interface ActionTile {
  key: SnapshotFilter;
  label: string;
  icon: LucideIcon;
  accentClass: string;
  value: number;
  tooltip: string;
  filterable: boolean;
  /** Optional indented sub-count lines rendered under the main value. */
  subCounts?: Array<{ label: string; value: number }>;
}


interface PerfTile {
  key: string;
  label: string;
  value: string;
  tooltip: string;
}

function readOpen(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

function writeOpen(open: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, open ? "true" : "false");
  } catch {
    /* ignore */
  }
}

function ActionSkeleton() {
  return (
    <div className="space-y-2">
      <div className="h-8 w-14 rounded bg-muted animate-pulse" />
      <div className="h-3 w-24 rounded bg-muted animate-pulse" />
      <div className="h-3 w-20 rounded bg-muted animate-pulse" />
    </div>
  );
}


function PerfSkeleton() {
  return (
    <div className="space-y-2">
      <div className="h-3 w-16 rounded bg-muted animate-pulse" />
      <div className="h-6 w-10 rounded bg-muted animate-pulse" />
      <div className="h-2.5 w-20 rounded bg-muted animate-pulse" />
    </div>
  );
}

export function MorningSnapshot({
  cases,
  loading,
  onFilter,
  vertical = DEFAULT_VERTICAL,
}: {
  cases: CaseSummary[];
  loading: boolean;
  onFilter?: (kind: SnapshotFilter) => void;
  /** The operator's vertical. Only the Review Complete tooltip reads it. */
  vertical?: Vertical;
}) {
  const [open, setOpen] = useState<boolean>(false);

  // Prefer the backend-aggregated summary (BACKEND_INTEGRATION.md §18) when
  // available; fall back to client-side computeSnapshot for older deploys.
  //
  // IMPORTANT: useDashboardSummary() (a hook) MUST be called before any
  // conditional return — Rules of Hooks. The earlier ordering (`useState`
  // → early return → useDashboardSummary) caused React error #300
  // ("Rendered fewer hooks than expected") on prod the moment cases became
  // empty (no applicants on a fresh tenant). UAT was masked because it
  // always had test cases, so the early return never fired. See prod
  // incident 2026-06-07 + ErrorBoundary capture in
  // digavouch-platform/.juan/rockwell.digavouch.com-*.log.
  const { data: summary } = useDashboardSummary();
  const local = computeSnapshot(cases);

  if (!loading && cases.length === 0) return null;

  // IMPORTANT: action-tile counts MUST come from `local` (computeSnapshot),
  // because the Dashboard queue filter also uses the local FE logic
  // (`getCaseStatus(c).kind === ...`). Mixing backend's
  // `waiting_on_applicant_count` with the local filter caused the
  // "tile says 3, queue shows 1" mismatch. Only borrow backend values for
  // the read-only performance metrics on the right side of the snapshot.
  const m: SnapshotMetrics = summary
    ? {
        ...local,
        // Trust backend for Waiting on Vouch — the FE proxy
        // (action_required && current_step >= 5) overcounts cases where the
        // operator requested non-vouch additional verification (e.g. ID
        // re-verify). Backend has the authoritative per-case vouch state.
        waiting_on_vouch_count:
          summary.awaiting_vouch_count ?? local.waiting_on_vouch_count,
        // Fall back to the local FE-derived value when the backend hasn't
        // computed avg time yet (older deploys, or null for very recent
        // sub-day completions that get truncated server-side).
        avg_time_to_complete_days:
          summary.avg_time_to_complete_days ?? local.avg_time_to_complete_days,
        verified_rate_percent: summary.verified_rate_percent,
        // Backend's `completion_rate_percent` == (verified+not_verified)/total
        // which matches our `review_complete_rate_percent`. The Submitted
        // rate is not exposed yet, so keep the FE-derived value.
        review_complete_rate_percent: summary.completion_rate_percent,
        completion_rate_percent: summary.completion_rate_percent,
        outcome_rate_percent: summary.completion_rate_percent,
      }
    : local;
  const reviewCompleteRate = m.review_complete_rate_percent;

  // Backend ships an authoritative count but no per-case identifier for
  // "awaiting vouch" today. Until CaseSummary exposes vouch_summary.status,
  // we can't filter the queue to exactly those rows — so render the tile as
  // read-only when we're relying on the backend count, to avoid showing the
  // user a filter that lies.
  const vouchTileFilterable = !summary;

  const actionTiles: ActionTile[] = [
    {
      key: "submitted",
      label: "Needs Review",
      icon: FileText,
      accentClass: "text-violet-600",
      value: m.needs_review_count,
      tooltip: "Cases ready for operator review.",
      filterable: true,
    },
    {
      key: "action_required",
      label: "Waiting on Applicant",
      icon: User,
      accentClass: "text-blue-600",
      value: m.waiting_on_applicant_count,
      tooltip: "Cases waiting on applicant action.",
      filterable: true,
    },
    {
      key: "waiting_on_vouch",
      label: "Waiting on Vouch",
      icon: HandHeart,
      accentClass: "text-violet-600",
      value: m.waiting_on_vouch_count,
      tooltip: vouchTileFilterable
        ? "Cases where one or more requested vouches are still pending."
        : "Cases where one or more requested vouches are still pending. Per-case filtering will unlock once the backend exposes vouch status on each case.",
      filterable: vouchTileFilterable,
    },
    {
      key: "needs_attention",
      label: "Idle",
      icon: AlertTriangle,
      accentClass: "text-orange-500",
      value: m.idle_count,
      tooltip: "Cases with no applicant or vouch progress in 2+ days.",
      filterable: true,
    },
  ];

  const perfTiles: PerfTile[] = [
    {
      key: "avg",
      label: "Avg. Time",
      value: formatAvgTime(m.avg_time_to_complete_days),
      tooltip: "Average time from invite sent to applicant submitted.",
    },
    {
      key: "submitted",
      label: "Submitted",
      value:
        m.submitted_rate_percent !== null
          ? `${m.submitted_rate_percent}%`
          : "—",
      tooltip: "Submitted applicants ÷ invited applicants.",
    },
    {
      key: "review_complete",
      label: "Review Complete",
      value:
        reviewCompleteRate !== null ? `${reviewCompleteRate}%` : "—",
      tooltip: isRecruiting(vertical)
        ? "Applicants your team finished reviewing ÷ invited applicants."
        : "Verified + not verified applicants ÷ invited applicants.",
    },
  ];

  const avgTimeDisplay = formatAvgTime(m.avg_time_to_complete_days);
  const collapsedSummary = `Needs Review: ${m.needs_review_count} · Waiting on Applicant: ${m.waiting_on_applicant_count} · Waiting on Vouch: ${m.waiting_on_vouch_count} · Idle: ${m.idle_count} · Avg. Time: ${avgTimeDisplay}`;

  const isLoadingTiles = loading && cases.length === 0;

  return (
    <section className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 sm:px-4 sm:py-3 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="morning-snapshot-body"
        className="w-full flex items-center justify-between gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
            <LineChart className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-slate-950 leading-none">
              Morning Snapshot
            </h2>
            {!open && (
              <span className="hidden sm:inline text-xs text-slate-500 tabular-nums truncate">
                {collapsedSummary}
              </span>
            )}
          </div>
        </div>
        <span className="shrink-0 flex items-center gap-1 text-xs text-slate-500">
          {open ? "Hide" : "Show"}
          {open ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </span>
      </button>

      {open && (
        <div
          id="morning-snapshot-body"
          className="mt-3 flex flex-col gap-3 md:flex-row md:items-stretch md:gap-5"
        >
          {/* Left group — Action Needed */}
          <div className="min-w-0 flex-[1.15]">
            <div className="mb-2 text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500">
              Action Needed
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-2 sm:gap-x-4 sm:gap-y-3 md:grid-cols-4">
              {isLoadingTiles
                ? Array.from({ length: 4 }).map((_, i) => (
                    <ActionSkeleton key={`a-skel-${i}`} />
                  ))
                : actionTiles.map((t) => {
                    const Icon = t.icon;
                    const tileClass =
                      "group min-w-0 rounded-md px-1 py-1 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";
                    const inner = (
                      <>
                        <div className="flex min-h-[2.25rem] items-start justify-center gap-1 sm:gap-1.5 text-[11px] sm:text-xs font-medium text-slate-600">
                          <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${t.accentClass}`} />
                          <span className="leading-snug break-words">{t.label}</span>
                        </div>
                        <div className="mt-1 text-base sm:text-lg font-semibold leading-tight text-slate-950 tabular-nums">
                          {t.value}
                        </div>
                        {t.subCounts && t.subCounts.length > 0 && (
                          <div className="mt-1 space-y-0.5 text-[10px] sm:text-[11px] text-slate-500 leading-tight">
                            {t.subCounts.map((sc) => (
                              <div key={sc.label}>
                                {sc.label}: <span className="tabular-nums">{sc.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                    return t.filterable ? (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() =>
                          onFilter?.(t.key)
                        }
                        title={t.tooltip}
                        className={`${tileClass} hover:bg-slate-50`}
                        aria-label={`Filter table by ${t.label}`}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div
                        key={t.key}
                        title={t.tooltip}
                        className={tileClass}
                        aria-label={t.label}
                      >
                        {inner}
                      </div>
                    );
                  })}
            </div>
          </div>

          {/* Divider: horizontal on mobile, vertical on md+ */}
          <div
            className="border-t border-slate-200 md:border-t-0 md:border-l md:self-stretch"
            aria-hidden="true"
          />

          {/* Right group — Performance */}
          <div className="min-w-0 flex-1">
            <div className="mb-2 mt-1 md:mt-0 text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500">
              Performance
            </div>
            <div className="grid grid-cols-3 gap-x-2 gap-y-2 sm:gap-x-3 sm:gap-y-3">
              {isLoadingTiles
                ? Array.from({ length: 3 }).map((_, i) => (
                    <PerfSkeleton key={`p-skel-${i}`} />
                  ))
                : perfTiles.map((t) => (
                    <div
                      key={t.key}
                      title={t.tooltip}
                      className="min-w-0 text-center"
                    >
                      <div className="flex min-h-[2.25rem] items-start justify-center text-[11px] sm:text-xs font-medium text-slate-500 leading-snug break-words">
                        {t.label}
                      </div>
                      <div className="mt-1 text-base sm:text-lg font-semibold leading-tight text-slate-950 tabular-nums">
                        {t.value}
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


